import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { AppConfig } from '@luggagehero/shared/app-settings/data-access';
import { ITimeInterval, StorageLocationBadgeType, StorageLocationItemBadge } from '@luggagehero/shared/interfaces';
import { SharedLocationService } from '@luggagehero/shared/services/locations';
import { SharedShopsService } from '@luggagehero/shared/services/shops';
import { TranslateService } from '@ngx-translate/core';

import { BaseComponent } from '../../../core';
import { ManageableStorageLocation } from '../../../services/storage-manager/storage-location';
import { StorageLocationItemAction } from '../../ui';

@Component({ template: '' })
export abstract class ManageShopItemBaseComponent extends BaseComponent implements OnInit {
  @Output() public viewBookings = new EventEmitter<void>();
  @Output() public viewTransactions = new EventEmitter<void>();
  @Output() public viewTaxSettings = new EventEmitter<void>();
  @Output() public viewInStoreDisplay = new EventEmitter<void>();
  @Output() public viewPricing = new EventEmitter<void>();
  @Output() public viewSettings = new EventEmitter<void>();

  @Input() public shop: ManageableStorageLocation;

  public averageRating = 0;
  public showAverageRating = true;
  public numberOfReviews = 0;
  public showNumberOfReviews = true;
  public isInitialized = false;

  public actions: StorageLocationItemAction[] = [];
  public badges: StorageLocationItemBadge[] = [];

  private storageLocationService = inject(SharedShopsService);
  private locationService = inject(SharedLocationService);
  private translate = inject(TranslateService);
  private cd = inject(ChangeDetectorRef);

  // Timestamp used for fetching opening hours
  private now = new Date();

  public get title(): string {
    return this.shop.name;
  }

  public get locationInfo(): string {
    return this.shop.address.street;
  }

  public get locationType(): string {
    if (!AppConfig.IS_STORAGE_LOCATION_TYPE_ENABLED) {
      return null;
    }

    let locationType: string;

    // TODO: Parse locationType to translation key
    switch (this.shop.locationType) {
      default:
        locationType = 'LUGGAGE_STORAGE';
        break;
    }

    return locationType;
  }

  public get openingHours(): ITimeInterval[] {
    return this.storageLocationService.getOpeningHoursForDate(this.now, this.shop);
  }

  public get nextOpenDay(): Date {
    return this.storageLocationService.getNextOpenDay(this.now, this.shop);
  }

  public get isMultiDayStorageAllowed(): boolean {
    return this.storageLocationService.isMultidayStorageAllowed(this.shop);
  }

  public get isRecommended(): boolean {
    return this.storageLocationService.isRecommended(this.shop);
  }

  public get isOpenLate(): boolean {
    if (!AppConfig.IS_OPEN_LATE_BADGE_ON_MANAGE_SHOP_ITEM_ENABLED) {
      return false;
    }
    return this.storageLocationService.isOpenLate(this.now, this.shop);
  }

  public get isAlwaysOpen(): boolean {
    if (!AppConfig.IS_ALWAYS_OPEN_BADGE_ON_MANAGE_SHOP_ITEM_ENABLED) {
      return false;
    }
    return this.storageLocationService.isAlwaysOpen(this.now, this.now, this.shop);
  }

  public get isHighlyRated(): boolean {
    if (!AppConfig.IS_HIGHLY_RATED_BADGE_ON_MANAGE_SHOP_ITEM_ENABLED) {
      return false;
    }
    if (this.isRecommended) {
      // Don't show highly rated badge if shop is recommended
      return false;
    }
    return this.storageLocationService.isHighlyRated(this.shop);
  }

  public get isFullyBooked(): boolean {
    // Not implemented
    return false;
  }

  public get isOpen(): boolean {
    return this.storageLocationService.isOpen(this.now, this.shop);
  }

  public get isClosed(): boolean {
    return this.storageLocationService.isClosed(this.now, this.shop);
  }

  public get isSuperheroTerminologyEnabled(): boolean {
    return AppConfig.IS_SUPERHERO_TERMINOLOGY_ENABLED;
  }

  public get userCheckinRateHtml(): string {
    const userCheckinRate = this.shop.stats?.userCheckinRate;

    if (userCheckinRate === undefined || userCheckinRate === null) {
      return null;
    }

    const checkinRate = `${(this.shop.stats.userCheckinRate * 100).toFixed(0)}%`;
    const suffix = (this.translate.instant('CHECK_IN_RATE') as string)?.toLowerCase();

    return `<span class="strong-first-child"><span>${checkinRate}</span><span>${suffix}</span></span>`;
  }

  public get numberOfWalkinBookingsHtml(): string {
    const numberOfWalkins = this.shop.stats?.numberOfWalkinBookings;

    if (numberOfWalkins === undefined || numberOfWalkins === null) {
      return null;
    }

    const suffix = (this.translate.instant('WALK_INS') as string)?.toLowerCase();

    return `<span class="strong-first-child"><span>${numberOfWalkins}</span><span>${suffix}</span></span>`;
  }

  public get statsTooltipHeaderHtml(): string {
    return this.translate.instant('STORAGE_LOCATION_KPIS_TOOLTIP_HEADER') as string;
  }

  public get statsTooltipBodyHtml(): string {
    const last30Days = (this.translate.instant('LAST_30_DAYS') as string).toLowerCase();
    const part1 = this.translate.instant('STORAGE_LOCATION_KPIS_TOOLTIP_BODY_PART_1') as string;
    const subheading1 = `${this.translate.instant('CHECK_IN_RATE')} (${last30Days})`;
    const part2 = this.translate.instant('STORAGE_LOCATION_KPIS_TOOLTIP_BODY_PART_2') as string;
    const subheading2 = `${this.translate.instant('WALK_INS')} (${last30Days})`;
    const part3 = this.translate.instant('STORAGE_LOCATION_KPIS_TOOLTIP_BODY_PART_3') as string;

    return `${part1}<br/><br/><b>${subheading1}</b><br/>${part2}<br/><br/><b>${subheading2}</b><br/>${part3}`;
  }

  public async ngOnInit() {
    await this.locationService.init();

    this.numberOfReviews = this.shop?.stats?.numberOfRatings || 0;
    this.averageRating = this.shop?.stats?.averageRating || 0;

    this.showNumberOfReviews = this.numberOfReviews > 1;
    this.showAverageRating = this.averageRating > 0;

    this.initActions();
    this.initBadges();

    this.isInitialized = true;
    this.cd.markForCheck();
  }

  private initBadges() {
    if (this.isRecommended) {
      this.badges.push({
        type: StorageLocationBadgeType.Recommended,
        text: this.isSuperheroTerminologyEnabled ? 'SUPERHERO' : (this.translate.instant('RECOMMENDED') as string),
      });
    }

    if (this.isHighlyRated) {
      this.badges.push({
        type: StorageLocationBadgeType.HighlyRated,
        text: this.translate.instant('HIGHLY_RATED') as string,
      });
    }

    if (this.isOpenLate || this.isAlwaysOpen) {
      this.badges.push({
        type: StorageLocationBadgeType.OpenLate,
        text: this.translate.instant(this.isAlwaysOpen ? 'ALWAYS_OPEN' : 'OPEN_LATE') as string,
      });
    }
  }

  private initActions() {
    this.actions.push(
      {
        callToAction: 'SETTINGS',
        isPrimary: false,
        onClick: (event) => {
          event.stopPropagation();
          this.viewSettings.emit();
        },
      },
      {
        callToAction: 'VIEW_SHOP_BOOKINGS',
        isPrimary: false,
        onClick: (event) => {
          event.stopPropagation();
          this.viewBookings.emit();
        },
      },
      {
        callToAction: 'PRICING',
        isPrimary: false,
        onClick: (event) => {
          event.stopPropagation();
          this.viewPricing.emit();
        },
      },
      {
        callToAction: 'BILLING',
        isPrimary: false,
        onClick: (event) => {
          event.stopPropagation();
          this.viewTransactions.emit();
        },
      },
      {
        callToAction: 'HERO_CODE',
        isPrimary: true,
        onClick: (event) => {
          event.stopPropagation();
          this.viewInStoreDisplay.emit();
        },
      },
    );

    const shopCountryCode = this.shop?.address?.countryCode || null;

    if (
      AppConfig.STORAGE_MANAGER_TAX_SETTINGS_ENABLED_COUNTRIES.includes(shopCountryCode) &&
      AppConfig.STORAGE_MANAGER_ENABLE_TAX_FEATURE
    ) {
      this.actions.push({
        callToAction: this.translate.instant('TAXES') as string,
        isPrimary: false,
        onClick: (event) => {
          event.stopPropagation();
          this.viewTaxSettings.emit();
        },
      });
    }
  }
}
